import React, { useState } from 'react';
import { X } from 'lucide-react';
import { api } from '../utils/api';
import SchedulingLink from './SchedulingLink';
import KnowledgeBaseManager from './KnowledgeBaseManager';
import VoiceSelect from './VoiceSelect';

interface DoubleSettingsModalProps {
  double: {
    id: string;
    name: string;
    scheduling_link: string | null;
    prompt: string | null;
    first_message: string | null;
    knowledge_base: string | null;
    meeting_duration: number | null;
    voice_id?: string | null;
    voice_name?: string | null;
    voice_type?: string | null;
  };
  onClose: () => void;
  onUpdate: (id: string, data: any) => void;
}

const DoubleSettingsModal = ({ double, onClose, onUpdate }: DoubleSettingsModalProps) => {
  const [activeTab, setActiveTab] = useState<'settings' | 'knowledge-base'>('settings');
  const [formData, setFormData] = useState({
    prompt: double.prompt || '',
    first_message: double.first_message || '',
    knowledge_base: double.knowledge_base || '',
    meeting_duration: double.meeting_duration ?? 1800,
    voice_id: double.voice_id || double.voice_type || '',
    voice_name: double.voice_name || null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await api.put<{ double: typeof double }>(`/doubles/${double.id}`, {
        prompt: formData.prompt,
        first_message: formData.first_message,
        knowledge_base: formData.knowledge_base,
        meeting_duration: formData.meeting_duration,
        voice_id: formData.voice_id || null,
        voice_name: formData.voice_name || null,
      });
      
      if (response?.double) {
        onUpdate(double.id, response.double);
      } else {
        onUpdate(double.id, formData);
      }
      onClose();
    } catch (err) {
      console.error('Error updating double:', err);
      setError('Failed to update double settings');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div 
        className="bg-white w-full max-w-2xl rounded-xl shadow-retro border-2 border-dark my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Settings for {double.name}</h2>
            <p className="text-gray-600 mt-1">Configure your double's behavior and scheduling</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 flex">
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-6 py-3 font-medium border-b-2 transition-colors ${
              activeTab === 'settings'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Settings
          </button>
          <button
            onClick={() => setActiveTab('knowledge-base')}
            className={`px-6 py-3 font-medium border-b-2 transition-colors ${
              activeTab === 'knowledge-base'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Knowledge Base
          </button>
        </div>

        {/* Settings Tab */}
        {activeTab === 'settings' && (
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <SchedulingLink 
            doubleId={double.id}
            initialName={double.scheduling_link?.replace('https://mycal.live/', '')}
          />

          <div>
            <label htmlFor="meetingDuration" className="block text-sm font-medium text-gray-700 mb-2">
              How long will the meeting be?
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                id="meetingDuration"
                min="300"
                max="1800"
                value={formData.meeting_duration}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 1800;
                  const duration = Math.max(300, Math.min(1800, value));
                  setFormData(prev => ({ ...prev, meeting_duration: duration }));
                }}
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
              <span className="text-gray-600">seconds</span>
            </div>
          </div>

          <div>
            <label htmlFor="prompt" className="block text-sm font-medium text-gray-700 mb-2">
              System Prompt
            </label>
            <textarea
              id="prompt"
              value={formData.prompt}
              onChange={(e) => setFormData(prev => ({ ...prev, prompt: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary h-32"
              placeholder="Enter the system instructions for your double..."
            />
          </div>

          <VoiceSelect
            value={formData.voice_id}
            onChange={(voiceId) => setFormData(prev => ({ ...prev, voice_id: voiceId }))}
            onSelect={(voice) => setFormData(prev => ({ ...prev, voice_name: voice.name }))}
            label="Voice"
            helpText="Choose the voice your double will use."
          />

          <div>
            <label htmlFor="firstMessage" className="block text-sm font-medium text-gray-700 mb-2">
              First Message
            </label>
            <textarea
              id="firstMessage"
              value={formData.first_message}
              onChange={(e) => setFormData(prev => ({ ...prev, first_message: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary h-24"
              placeholder="Enter the first message your double will say..."
            />
          </div>

          <div>
            <label htmlFor="knowledgeBase" className="block text-sm font-medium text-gray-700 mb-2">
              Knowledge Base
            </label>
            <textarea
              id="knowledgeBase"
              value={formData.knowledge_base}
              onChange={(e) => setFormData(prev => ({ ...prev, knowledge_base: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary h-40"
              placeholder="Enter the knowledge base for your double..."
            />
          </div>

          {error && (
            <div className="text-sm text-red-600">{error}</div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-1"></span>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
        )}

        {/* Knowledge Base Tab */}
        {activeTab === 'knowledge-base' && (
          <div className="p-6">
            <KnowledgeBaseManager doubleId={double.id} onClose={() => setActiveTab('settings')} />
          </div>
        )}
      </div>
    </div>
  );
};

export default DoubleSettingsModal;
